import React from "react";
import { ArrowRight } from "lucide-react";
import Photo from "./Photo";

// Worked examples that map directly to the planner's pricing tiers.
// Sets ballpark expectations and pre-qualifies budget before the slider.
const EXAMPLES = [
  {
    label: "Café menu refresh",
    images: 18,
    rate: 30,
    total: 540,
    tier: "15–20 images · $30/image",
    photo: "menu-malai-tikka",
    note: "Best for cafés re-shooting a hero set across breakfast and lunch.",
  },
  {
    label: "Restaurant menu library",
    images: 35,
    rate: 25,
    total: 875,
    tier: "21–99 images · $25/image",
    photo: "menu-lamb-dosa",
    note: "Most common booking — full lunch and dinner menu in a single day.",
    featured: true,
  },
  {
    label: "Volume catalogue",
    images: 100,
    rate: 10,
    total: 1000,
    tier: "100+ images · $10/image",
    photo: "hero-spread-01",
    note: "For franchise operators, multi-location restaurants and catering brands.",
  },
];

export const SamplePricing = () => (
  <section
    data-testid="sample-pricing"
    className="bg-bone text-ink border-t border-ink/10"
  >
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10 py-16 lg:py-24">
      <div className="grid grid-cols-12 gap-6 lg:gap-10 mb-10 lg:mb-14 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 font-bold">
            Typical shoots · What others paid
          </p>
          <h3
            className="font-display tracking-editorial leading-[0.95]"
            style={{ fontSize: "clamp(1.875rem, 4vw, 3.25rem)" }}
          >
            Three real-world examples
            <br />
            <span className="italic font-light text-ember">before you tweak the slider.</span>
          </h3>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed max-w-md">
            Indicative pricing — actual quote confirmed via a quick phone call.
            Most Melbourne café and restaurant shoots land in the middle tier.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {EXAMPLES.map((ex) => (
          <article
            key={ex.label}
            data-testid={`sample-${ex.images}`}
            className={`relative flex flex-col border-2 ${
              ex.featured
                ? "border-ember bg-ink text-bone"
                : "border-ink/15 bg-[#FBF7F0] text-ink hover:border-ink transition-colors"
            }`}
          >
            {ex.featured && (
              <span className="absolute -top-3 left-6 bg-ember text-bone text-[10px] uppercase tracking-wider2 font-bold px-3 py-1.5">
                Most common
              </span>
            )}

            <div className="relative">
              <Photo
                slug={ex.photo}
                alt={`${ex.label} — Melbourne menu photography example`}
                ratio="16/9"
                sizes="(min-width: 768px) 33vw, 100vw"
              />
            </div>

            <div className="p-6 md:p-8 flex flex-col flex-1">
              <p className={`text-xs uppercase tracking-wider2 font-bold mb-3 ${ex.featured ? "text-ember" : "text-char-mute"}`}>
                {ex.tier}
              </p>
              <h4 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight">
                {ex.label}
              </h4>
              <p className={`text-sm leading-relaxed mt-3 flex-1 ${ex.featured ? "text-bone/75" : "text-char"}`}>
                {ex.note}
              </p>

              <div className={`mt-6 pt-6 border-t ${ex.featured ? "border-bone/15" : "border-ink/10"}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <p className={`text-xs uppercase tracking-wider2 mb-1 ${ex.featured ? "text-bone/60" : "text-char-mute"}`}>
                      {ex.images} images
                    </p>
                    <p className="font-sans font-bold tracking-tight leading-none" style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}>
                      ${ex.total.toLocaleString()}
                    </p>
                  </div>
                  <span className={`text-xs uppercase tracking-wider2 font-bold ${ex.featured ? "text-ember" : "text-char-mute"}`}>
                    AUD · ex GST
                  </span>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-10 lg:mt-14 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-8 border-t border-ink/10">
        <p className="text-sm text-char-mute max-w-xl">
          Your shoot doesn&apos;t fit these brackets exactly? Use the estimator
          below to dial it in — or just call directly.
        </p>
        <a href="#planner" className="inline-flex items-center gap-3 text-ember hover:text-ink transition-colors group">
          <span className="text-sm uppercase tracking-wider2 font-bold">
            Get a precise estimate
          </span>
          <ArrowRight size={16} strokeWidth={2} className="transition-transform group-hover:translate-x-1" />
        </a>
      </div>
    </div>
  </section>
);

export default SamplePricing;
